import { Header } from '@/components/layouts/Header'
import { Sidebar, type MenuItemProps } from '@/components/layouts/Sidebar'
import { Tooltip } from '@/components/Tooltip'
import { DashboardExperiments } from '@/experiments'
import { useTemplateManagementPermissions } from '@/hooks/permissions/templateManagement/useTemplateManagementPermissions'
import { useIsMarqiiUser } from '@/hooks/permissions/useIsMarqiiUser'
import { MarketIntelligencePages } from '@/routes/MarketIntelligencePages'
import { PATHS } from '@/routes/PATHS'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import { OVERLAY_ID } from '@/utils/constants/elements'
import {
  AlertOutlined,
  AppstoreAddOutlined,
  AppstoreOutlined,
  BellOutlined,
  CommentOutlined,
  ContainerOutlined,
  FileTextOutlined,
  FireOutlined,
  FormOutlined,
  MessageOutlined,
  PieChartOutlined,
  SearchOutlined,
  ShopOutlined,
  StarOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import { useFlag } from '@unleash/proxy-client-react'
import { Divider, Layout } from 'antd'
import { Lightbulb, MessageSquarePlus, RectangleEllipsis } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import styles from './styles.module.scss'

export const PAGE_LAYOUT_TEST_ID = 'page-layout-test-id'
export const PAGE_LAYOUT_CONTENT_ID = 'page-layout-content-id'
const SIDEBAR_WIDTH = 200
const ICON_SIZE = 14

export function PageLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const { pathname } = useLocation()
  const layoutContentRef = useRef<HTMLDivElement | null>(null)
  const { isMarqiiUser } = useIsMarqiiUser()
  const { shouldShowTemplatesManagement } = useTemplateManagementPermissions()

  const hideMenusForLocationManagersFlag = useFlag(
    DashboardExperiments.hideMenusForLocationManagers,
  )
  const firstPartyReviewsFlag = useFlag(DashboardExperiments.firstPartyReviews)
  const marketIntelligenceFlag = useFlag(DashboardExperiments.showMarketIntelligence)
  const reviewGenerationFlag = useFlag(DashboardExperiments.reviewGeneration)

  const listingsManagementPermission = useCorePermissions(CoreLevelFeatureNames.listingsManagement)
  const userManagementPermission = useCorePermissions(CoreLevelFeatureNames.userManagement)
  const menuManagementPermission = useCorePermissions(CoreLevelFeatureNames.menuManagement)
  const responsePromptsPermission = useCorePermissions(CoreLevelFeatureNames.promptManagement)
  const reportsPermission = useCorePermissions(CoreLevelFeatureNames.emailedReports)
  const labelManagementPermission = useCorePermissions(CoreLevelFeatureNames.labelManagement)
  const socialManagementPermission = useCorePermissions(CoreLevelFeatureNames.socialManagementPosts)

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )
  const hasMenusFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Menus)
  const hasManagedReviewsExternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsExternal,
  )
  const hasManagedReviewsInternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsInternal,
  )
  const hasListingsFeature = premiumFeaturesPermissions.includes(PremiumFeatures.Listings)
  const hasSocialManagementFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.SocialManagement,
  )
  const hasWidgetMenusFeature =
    premiumFeaturesPermissions.includes(PremiumFeatures.WidgetMenus) && hasMenusFeature
  const hasWidgetHoursFeature = premiumFeaturesPermissions.includes(PremiumFeatures.WidgetHours)
  const hasWidgetReviewFormsFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.WidgetReviewForms,
  )

  const hasUserManagementPermission = useMemo(() => {
    return !!userManagementPermission?.edit
  }, [userManagementPermission])

  const hasMenuManagementPermission = useMemo(() => {
    return !!menuManagementPermission?.edit
  }, [menuManagementPermission])

  const hasResponsePromptPermission = useMemo(() => {
    return !!responsePromptsPermission?.edit
  }, [responsePromptsPermission])

  const hasReportsPermission = useMemo(() => {
    return !!reportsPermission?.edit
  }, [reportsPermission])

  const hasLabelManagementPermission = useMemo(() => {
    return !!labelManagementPermission?.edit
  }, [labelManagementPermission])

  const shouldHideMenuPageItem = useMemo(() => {
    return !!menuManagementPermission?.hide
  }, [menuManagementPermission])

  const shouldHideListingsPageItem = useMemo(() => {
    return !!listingsManagementPermission?.hide
  }, [listingsManagementPermission])

  const hasSocialManagementPermission = useMemo(() => {
    return !!socialManagementPermission?.view
  }, [socialManagementPermission])

  const hasHoursWidgetsManagementPermission = useMemo(() => {
    return !shouldHideListingsPageItem
  }, [shouldHideListingsPageItem])

  const hasMenusWidgetsManagementPermission = useMemo(() => {
    return !shouldHideMenuPageItem
  }, [shouldHideMenuPageItem])

  const hasFirstPartyReviewsPermission = useMemo(() => {
    return !shouldHideListingsPageItem
  }, [shouldHideListingsPageItem])

  const isLocationManager = hasMenuManagementPermission && !hasUserManagementPermission

  const shouldHideMenusForLocationManagers = hideMenusForLocationManagersFlag && isLocationManager

  const hideMenuPageItem = !hasMenusFeature || shouldHideMenuPageItem

  const hideListingsPageItem = shouldHideListingsPageItem || !hasListingsFeature

  const hasManagedReviewsInternalFeatureAndIsMarqiiUser =
    isMarqiiUser && hasManagedReviewsInternalFeature

  const shouldShowResponsePrompts =
    (hasManagedReviewsExternalFeature || hasManagedReviewsInternalFeatureAndIsMarqiiUser) &&
    hasResponsePromptPermission

  const shouldShowLabelManagementPage = hasReviewMonitoringFeature && hasLabelManagementPermission

  const shouldShowSocialManagementButton =
    hasSocialManagementFeature && hasSocialManagementPermission

  const shouldShowWidgets =
    (hasWidgetMenusFeature && hasMenusWidgetsManagementPermission) ||
    (hasWidgetHoursFeature && hasHoursWidgetsManagementPermission)

  const shouldShowFirstPartyReviewsPage =
    hasWidgetReviewFormsFeature && hasFirstPartyReviewsPermission && firstPartyReviewsFlag

  const allReviewsItems = [
    {
      key: PATHS.reviewsPage,
      label: t('sidebarItems.monitoring'),
      path: PATHS.reviewsPage,
      icon: <CommentOutlined />,
    },
    {
      key: PATHS.reviewGenerationPage,
      label: t('sidebarItems.reviewGeneration'),
      path: PATHS.reviewGenerationPage,
      icon: <MessageSquarePlus size={ICON_SIZE} />,
    },
    {
      key: PATHS.firstPartyReviews,
      label: t('sidebarItems.firstPartyReviews'),
      path: PATHS.firstPartyReviews,
      icon: <RectangleEllipsis size={ICON_SIZE} />,
    },
    {
      key: PATHS.reviewResponsePromptsPage,
      label: t('sidebarItems.responsePrompts'),
      path: PATHS.reviewResponsePromptsPage,
      icon: <MessageOutlined />,
    },
    {
      key: PATHS.reviewTemplatesPage,
      label: t('sidebarItems.templates'),
      path: PATHS.reviewTemplatesPage,
      icon: <FileTextOutlined />,
    },
    {
      key: PATHS.reviewLabelsPage,
      label: t('sidebarItems.labels'),
      path: PATHS.reviewLabelsPage,
      icon: <StarOutlined />,
    },
    {
      key: PATHS.reviewNotificationsPage,
      label: t('sidebarItems.notifications'),
      path: PATHS.reviewNotificationsPage,
      icon: <BellOutlined />,
    },
  ]
  const availableReviewsItems = allReviewsItems
    .filter((item) => shouldShowResponsePrompts || item.key !== PATHS.reviewResponsePromptsPage)
    .filter((item) => shouldShowLabelManagementPage || item.key !== PATHS.reviewLabelsPage)
    .filter((item) => shouldShowTemplatesManagement || item.key !== PATHS.reviewTemplatesPage)
    .filter((item) => shouldShowFirstPartyReviewsPage || item.key !== PATHS.firstPartyReviews)
    .filter((item) => reviewGenerationFlag || item.key !== PATHS.reviewGenerationPage)

  const allAnalyticsItems = [
    {
      key: PATHS.analyticsPage,
      label: t('sidebarItems.customerInteractions'),
      path: PATHS.analyticsPage,
      icon: <PieChartOutlined />,
    },
    {
      key: PATHS.searchAppearancesPage,
      label: t('sidebarItems.searchAppearances'),
      path: PATHS.searchAppearancesPage,
      icon: <SearchOutlined />,
    },
    {
      key: PATHS.reviewAnalysis,
      label: t('sidebarItems.reviewsInsights'),
      path: PATHS.reviewAnalysis,
      icon: <FireOutlined />,
    },
    {
      key: PATHS.emailReportsPage,
      label: t('sidebarItems.reports'),
      path: PATHS.emailReportsPage,
      icon: <AlertOutlined />,
    },
    {
      key: PATHS[MarketIntelligencePages.locationsTable],
      label: t('sidebarItems.marketIntelligence'),
      path: PATHS[MarketIntelligencePages.locationsTable],
      icon: <Lightbulb size={ICON_SIZE} />,
    },
  ]
  const availableAnalyticsItems = allAnalyticsItems
    .filter((item) => (hasReportsPermission ? true : item.key !== PATHS.emailReportsPage))
    .filter((item) => (hasReviewMonitoringFeature ? true : item.key !== PATHS.reviewAnalysis))
    .filter((item) =>
      marketIntelligenceFlag ? true : item.key !== PATHS[MarketIntelligencePages.locationsTable],
    )

  const allSettingsItems = [
    {
      key: PATHS.accountConnectionsPage,
      label: t('sidebarItems.connections'),
      path: PATHS.accountConnectionsPage,
      icon: <ThunderboltOutlined />,
    },
    {
      key: PATHS.userManagementPage,
      label: t('sidebarItems.userManagement'),
      path: PATHS.userManagementPage,
      icon: <UsergroupAddOutlined />,
    },
  ]
  const availableSettingsItems = allSettingsItems.filter((item) =>
    hasUserManagementPermission ? true : item.key !== PATHS.userManagementPage,
  )

  const allSidebarItems: MenuItemProps[] = [
    {
      icon: <AppstoreOutlined />,
      key: PATHS.homePage,
      path: PATHS.homePage,
      label: t('sidebarItems.home'),
    },
    {
      icon: <ShopOutlined />,
      key: PATHS.locationsPage,
      path: PATHS.locationsPage,
      label: t('sidebarItems.locations'),
    },
    {
      icon: <SyncOutlined />,
      key: PATHS.listingsPage,
      label: (
        <Tooltip title={t('sidebarItems.listings.tooltipText')} placement="right">
          {t('sidebarItems.listings.text')}
        </Tooltip>
      ),
      path: PATHS.listingsPage,
    },
    {
      icon: <ContainerOutlined />,
      key: PATHS.menusPage,
      label: t('sidebarItems.menus'),
      path: PATHS.menusPage,
    },
    {
      icon: <FormOutlined />,
      key: PATHS.publishedTab,
      label: t('sidebarItems.socialManagement'),
      path: PATHS.publishedTab,
    },
    {
      icon: <AppstoreAddOutlined />,
      key: PATHS.widgetsList,
      label: t('sidebarItems.widgets'),
      path: PATHS.widgetsList,
    },
    {
      icon: null,
      key: 'reviewsGroup',
      label: collapsed ? '' : t('sidebarItems.reviews'),
      children: availableReviewsItems,
      type: 'group',
    },
    {
      icon: null,
      key: 'analyticsGroup',
      label: collapsed ? '' : t('sidebarItems.analytics'),
      children: availableAnalyticsItems,
      type: 'group',
    },
    {
      icon: null,
      key: 'settingsGroup',
      label: collapsed ? '' : t('sidebarItems.settings'),
      children: availableSettingsItems,
      type: 'group',
    },
  ]
  const availableSidebarItems = allSidebarItems.filter((item) =>
    hasReviewMonitoringFeature ? true : item.key !== 'reviewsGroup',
  )

  const sidebarItems = availableSidebarItems
    .filter((item) => (shouldShowSocialManagementButton ? true : item.key !== PATHS.publishedTab))
    .filter((item) => (shouldShowWidgets ? true : item.key !== PATHS.widgetsList))
    .filter((item) => (hasReviewMonitoringFeature ? true : item.key !== 'reviews'))
    .filter((item) => (hideMenuPageItem ? item.key !== PATHS.menusPage : true))
    .filter((item) => (shouldHideMenusForLocationManagers ? item.key !== PATHS.menusPage : true))
    .filter((item) => (hideListingsPageItem ? item.key !== PATHS.listingsPage : true))

  useEffect(() => {
    if (layoutContentRef.current) {
      layoutContentRef.current.scrollTop = 0
    }
  }, [pathname])

  return (
    <Layout className={styles.pageLayout} data-testid={PAGE_LAYOUT_TEST_ID}>
      <Header />
      <Divider className={styles.headerDivider} />

      <Layout className={styles.backgroundWhite}>
        <Sidebar
          width={SIDEBAR_WIDTH}
          menuItems={sidebarItems}
          defaultOpenKeys={['reviews', PATHS.analyticsPage]}
          onSelect={({ key }) => navigate(key)}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
        <Layout.Content
          className={styles.pageContent}
          id={PAGE_LAYOUT_CONTENT_ID}
          ref={layoutContentRef}
        >
          <Outlet />
        </Layout.Content>
      </Layout>

      <div id={OVERLAY_ID} />
    </Layout>
  )
}
